import React from "react";
import { formatToPeso } from "../../../util/helperFunctions";

function RequestDetailItem({ prDetail, setPreviewImage, itemIndex = 0 }) {
  const getAttachmentUrl = (attachment, filePath) => {
    const resourcesUrl = process.env.REACT_APP_ATTACHMENT_DATA_RESOURCES_URL;

    if (filePath && filePath.startsWith("http")) {
      return filePath;
    }

    if (attachment && attachment.startsWith("http")) {
      return attachment;
    }

    return `${resourcesUrl}/${attachment}`;
  };

  const getFileName = (fileInfo = {}) => {
    const fallbackName =
      fileInfo?.original_name ||
      fileInfo?.filename ||
      fileInfo?.name ||
      fileInfo?.attachment ||
      fileInfo?.file_path ||
      "attachment";

    return fallbackName.toString().split("/").pop() || "attachment";
  };

  const isImageExtension = (url = "") => {
    const clean = url.toLowerCase().split("?")[0];
    return /\.(png|jpe?g|gif|webp|bmp)$/i.test(clean);
  };

  const isPdfExtension = (url = "") => {
    const clean = url.toLowerCase().split("?")[0];
    return clean.endsWith(".pdf");
  };

  const deriveFileType = (fileInfo = {}, fileName = "") => {
    if (fileInfo?.file_type) return fileInfo.file_type;

    const parts = fileName.split(".");
    if (parts.length < 2) return "Unknown";
    return parts[parts.length - 1].toUpperCase();
  };

  const renderFileRow = (fileInfo, index, sourceLabel) => {
    const attachmentUrl = getAttachmentUrl(fileInfo?.attachment, fileInfo?.file_path);
    const fileName = getFileName(fileInfo);
    const fileType = deriveFileType(fileInfo, fileName);
    const isImageAttachment = isImageExtension(attachmentUrl) || fileInfo?.file_type?.includes("image");
    const isPdfAttachment = isPdfExtension(attachmentUrl);
    const isPreviewableAttachment = isImageAttachment || isPdfAttachment;

    const iconClass = isPdfAttachment
      ? "fa-file-pdf-o"
      : isImageAttachment
      ? "fa-file-image-o"
      : "fa-file-o";

    const actionLabel = isPreviewableAttachment ? "Preview" : "Open";

    const fileSizeLabel = fileInfo?.file_size || "Unknown";

    return (
      <div className="request-line-file" key={`${sourceLabel}-${index}-${fileName}`}>
        <div className="request-line-file__meta">
          <i className={`fa ${iconClass}`} aria-hidden="true"></i>
          <div>
            <strong>{fileName}</strong>
            <span>
              {sourceLabel} | {fileType} | Size: {fileSizeLabel}
            </span>
          </div>
        </div>

        <div className="request-line-file__actions">
          <button
            type="button"
            className="request-inline-action"
            onClick={() => {
              if (isPreviewableAttachment) {
                setPreviewImage(attachmentUrl);
                return;
              }

              window.open(attachmentUrl, "_blank", "noopener,noreferrer");
            }}
          >
            {actionLabel}
          </button>
          <a
            href={attachmentUrl}
            className="request-inline-action"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download
          </a>
        </div>
      </div>
    );
  };

  const hasDescription = Boolean(prDetail?.description?.trim());
  const hasRemarks = Boolean(prDetail?.remarks?.trim());
  const hasAttachments = (prDetail?.purchases_attachment_detail || []).length > 0;
  const hasFiles = (prDetail?.purchases_files_detail || []).length > 0;

  return (
    <article className="request-line-item">
      <header className="request-line-item__header">
        <div>
          <span className="request-line-item__eyebrow">Line Item {itemIndex + 1}</span>
          <h3 className="request-line-item__title">
            {hasDescription ? prDetail?.description : "No description provided"}
          </h3>
        </div>
        <strong>{formatToPeso(prDetail?.estimated_cost || 0)}</strong>
      </header>

      <div className="request-line-item__metrics">
        <div>
          <span>Quantity</span>
          <strong>{prDetail?.quantity || 0}</strong>
        </div>
        <div>
          <span>Estimated Price</span>
          <strong>{formatToPeso(prDetail?.estimated_price || 0)}</strong>
        </div>
        <div>
          <span>Estimated Cost</span>
          <strong>{formatToPeso(prDetail?.estimated_cost || 0)}</strong>
        </div>
      </div>

      <div className="request-line-item__description">
        <h4>Remarks</h4>
        <p className="request-line-item__remarks">
          {hasRemarks ? prDetail?.remarks : "No remarks provided"}
        </p>
      </div>

      <div className="request-line-item__files">
        <h4>Attachments and Files</h4>

        {hasAttachments || hasFiles ? (
          <div className="request-line-files-list">
            {(prDetail?.purchases_attachment_detail || []).map((prAttachment, index) =>
              renderFileRow(prAttachment, index, "Attachment"),
            )}

            {(prDetail?.purchases_files_detail || []).map((prAttachment, index) =>
              renderFileRow(prAttachment, index, "File"),
            )}
          </div>
        ) : (
          <p className="request-line-files-empty">No files attached to this line item.</p>
        )}
      </div>
    </article>
  );
}

export default RequestDetailItem;
